// Plays an alert sound when a new active alarm appears.
//
// Designed to be reliable on an unattended control-room panel:
//   - A shared AudioContext is created eagerly and kept "warm" (resumed on
//     visibility/focus changes and on a periodic tick) so the browser does not
//     leave it suspended.
//   - A real alarm tone (static/sounds/alarm.wav) is preloaded and decoded into
//     an AudioBuffer. If it cannot be loaded, playback falls back to a
//     synthesized two-beep tone so an alert is always produced.
//   - On a normal browser tab, audio stays blocked until the first user gesture
//     (autoplay policy); we unlock on the first interaction. On a kiosk launched
//     with autoplay allowed (e.g. Chromium
//     --autoplay-policy=no-user-gesture-required) the context starts running and
//     no gesture is required.

const ALARM_SOUND_URL = '/static/sounds/alarm.wav'
const KEEP_WARM_INTERVAL_MS = 20000

let audioCtx = null
let alarmBuffer = null
let bufferLoadStarted = false
let unlockListenersInstalled = false
let keepWarmTimer = null

// Continuous-loop playback state. While at least one audible (non-muted) active
// alarm exists the tone repeats until it is explicitly stopped.
let loopingSource = null
let loopingBeepTimer = null
let isLooping = false

function getAudioContext () {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    return null
  }
  if (!audioCtx) {
    audioCtx = new AudioContextClass()
  }
  return audioCtx
}

function resumeContext () {
  const ctx = getAudioContext()
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }
}

// Loads and decodes the alarm tone into an AudioBuffer. Best-effort: on failure
// playback falls back to the synthesized beep.
function preloadAlarmBuffer () {
  if (bufferLoadStarted || typeof fetch === 'undefined') {
    return
  }
  bufferLoadStarted = true

  const ctx = getAudioContext()
  if (!ctx) {
    return
  }

  fetch(ALARM_SOUND_URL)
    .then(response => response.arrayBuffer())
    .then(data => new Promise((resolve, reject) => {
      // Use the callback form for broad (older Safari) compatibility.
      ctx.decodeAudioData(data, resolve, reject)
    }))
    .then(buffer => {
      alarmBuffer = buffer
    })
    .catch(() => {
      // Keep alarmBuffer null so playback falls back to the beep.
    })
}

// Resumes the AudioContext and plays a silent buffer. Running this inside a
// real user-gesture handler satisfies the browser autoplay policy so later
// programmatic playback is permitted.
function unlockAudio () {
  const ctx = getAudioContext()
  if (!ctx) {
    return
  }
  resumeContext()
  try {
    const buffer = ctx.createBuffer(1, 1, 22050)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start(0)
  } catch (err) {
    // Ignore: best-effort unlock.
  }
}

// Installs one-time listeners so the audio context is unlocked the first time
// the user interacts with the page, keeps the context warm across visibility
// changes, and preloads the alarm tone. Safe to call multiple times.
export function initAlarmSound () {
  if (unlockListenersInstalled || typeof document === 'undefined') {
    return
  }
  unlockListenersInstalled = true

  getAudioContext()
  preloadAlarmBuffer()

  const handler = () => {
    unlockAudio()
    document.removeEventListener('click', handler)
    document.removeEventListener('keydown', handler)
    document.removeEventListener('touchstart', handler)
  }

  document.addEventListener('click', handler)
  document.addEventListener('keydown', handler)
  document.addEventListener('touchstart', handler)

  // Keep the context running: browsers may suspend it after inactivity or when
  // the tab is hidden, which would silence later alarms on an unattended panel.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resumeContext()
    }
  })
  window.addEventListener('focus', resumeContext)

  if (!keepWarmTimer) {
    keepWarmTimer = setInterval(resumeContext, KEEP_WARM_INTERVAL_MS)
  }
}

function playBeep (ctx, startTime, frequency) {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillator.type = 'sine'
  oscillator.frequency.value = frequency

  gain.gain.setValueAtTime(0.0001, startTime)
  gain.gain.exponentialRampToValueAtTime(0.2, startTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.15)

  oscillator.connect(gain)
  gain.connect(ctx.destination)

  oscillator.start(startTime)
  oscillator.stop(startTime + 0.16)
}

// Fallback alert when the alarm tone buffer is unavailable.
function scheduleBeeps (ctx) {
  const now = ctx.currentTime
  playBeep(ctx, now, 880)
  playBeep(ctx, now + 0.18, 880)
}

function playAlarmBuffer (ctx) {
  const source = ctx.createBufferSource()
  source.buffer = alarmBuffer
  source.connect(ctx.destination)
  source.start(0)
}

function emit (ctx) {
  if (alarmBuffer) {
    playAlarmBuffer(ctx)
  } else {
    scheduleBeeps(ctx)
  }
}

export function playAlarmSound () {
  try {
    const ctx = getAudioContext()
    if (!ctx) {
      return
    }
    // Lazily start preloading if init() never ran for some reason.
    preloadAlarmBuffer()

    if (ctx.state === 'suspended') {
      // Resume is async; only emit once the context is actually running,
      // otherwise scheduled audio is queued against a clock that isn't advancing.
      ctx.resume().then(() => emit(ctx)).catch(() => {})
      return
    }
    emit(ctx)
  } catch (err) {
    // Audio unavailable or blocked by the browser autoplay policy: ignore.
  }
}

// Tears down any currently scheduled looping nodes/timers without changing the
// desired loop state.
function stopLoopingNodes () {
  if (loopingSource) {
    try {
      loopingSource.onended = null
      loopingSource.stop(0)
    } catch (err) {
      // Already stopped.
    }
    try {
      loopingSource.disconnect()
    } catch (err) {
      // Already disconnected.
    }
    loopingSource = null
  }
  if (loopingBeepTimer) {
    clearInterval(loopingBeepTimer)
    loopingBeepTimer = null
  }
}

function beginLoop (ctx) {
  if (!isLooping) {
    return
  }
  stopLoopingNodes()

  if (alarmBuffer) {
    // A real tone is available: loop the buffer seamlessly.
    const source = ctx.createBufferSource()
    source.buffer = alarmBuffer
    source.loop = true
    source.connect(ctx.destination)
    source.start(0)
    loopingSource = source
  } else {
    // No decoded tone yet: repeat the synthesized beep and upgrade to the real
    // tone as soon as it finishes loading.
    scheduleBeeps(ctx)
    loopingBeepTimer = setInterval(() => {
      if (!isLooping) {
        return
      }
      if (alarmBuffer) {
        beginLoop(ctx)
        return
      }
      scheduleBeeps(ctx)
    }, 1500)
  }
}

// Starts (or keeps) the alarm tone looping continuously until stopAlarmSound()
// is called. Safe to call repeatedly: it is a no-op while already looping.
export function startAlarmLoop () {
  try {
    const ctx = getAudioContext()
    if (!ctx) {
      return
    }
    if (isLooping) {
      return
    }
    isLooping = true
    preloadAlarmBuffer()

    if (ctx.state === 'suspended') {
      ctx.resume().then(() => beginLoop(ctx)).catch(() => {})
      return
    }
    beginLoop(ctx)
  } catch (err) {
    // Audio unavailable or blocked by the browser autoplay policy: ignore.
  }
}

// Stops the continuous alarm tone.
export function stopAlarmSound () {
  isLooping = false
  stopLoopingNodes()
}

// Whether the alarm tone is currently looping.
export function isAlarmLooping () {
  return isLooping
}

// Returns the current audio availability so the UI can show whether the alarm
// sound will actually play: 'running' (audible), 'suspended' (blocked until a
// gesture), or 'unavailable' (no Web Audio support).
export function getAudioStatus () {
  const ctx = getAudioContext()
  if (!ctx) {
    return 'unavailable'
  }
  return ctx.state === 'running' ? 'running' : 'suspended'
}

// Enables the alarm sound from a user gesture (e.g. clicking the indicator).
// Resumes/unlocks the context and plays a short confirmation tone. Returns a
// promise that resolves to the resulting audio status.
export function enableAlarmSound () {
  const ctx = getAudioContext()
  if (!ctx) {
    return Promise.resolve('unavailable')
  }
  preloadAlarmBuffer()
  const finish = () => {
    try {
      // Short confirmation so the operator hears that sound now works.
      playBeep(ctx, ctx.currentTime, 880)
    } catch (err) {
      // Ignore.
    }
    return getAudioStatus()
  }
  unlockAudio()
  if (ctx.state === 'suspended') {
    return ctx.resume().then(finish).catch(() => getAudioStatus())
  }
  return Promise.resolve(finish())
}



// WEBPACK FOOTER //
// ./src/utils/alarm-sound.js